import {
   Dialog,
   Flex,
   TextField,
   Text,
   DataList,
   Button,
   Separator,
   RadioGroup,
   Card,
} from '@radix-ui/themes';
import { Validators } from 'core/services/Validators';
import { makeAutoObservable } from 'mobx';
import { observer } from 'mobx-react-lite';
import { ReactNode, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { DialogHeader, FormField } from 'shared/components';
import { FormControl } from 'shared/libs/form';

import { HttpClient } from 'shared/libs/http';
import { AsyncState } from 'shared/libs/state';
import { ApiError } from 'shared/types';
import { toast } from 'sonner';
import {
   PlansOrderPaymentType,
   PlansOrderRequestPayload,
   PlansOrderResponse,
   PurchasePlan,
} from '../plans-purchase.types';

type StoreCallbacks = {
   onError?: (message: string) => void;
   onAuthError?: () => void;
};

class PlanPurchaseStore {
   readonly orderPlanState = new AsyncState<PlansOrderResponse>();

   private readonly payerControl = new FormControl<string>('', [Validators.required]);
   paymentType: PlansOrderPaymentType = 'card';
   constructor() {
      makeAutoObservable(this, undefined, {
         autoBind: true,
      });
   }

   setPaymentType(p: PlansOrderPaymentType): void {
      this.paymentType = p;
   }

   get payer(): string {
      return this.payerControl.value;
   }

   get payerError(): null | string {
      return this.payerControl.error;
   }

   get ordering(): boolean {
      return this.orderPlanState.status === 'pending';
   }

   setPayer(p: string): void {
      this.payerControl.setValue(p);
   }

   async orderPlan({
      planId,
      paymentType,
      onSuccess,
      onError,
      onAuthError,
   }: StoreCallbacks & {
      planId: number;
      paymentType?: PlansOrderPaymentType;
      payer?: string;
      onSuccess?: (message?: string, response?: PlansOrderResponse) => void;
   }): Promise<void> {
      this.payerControl.validate();

      if (this.payerControl.invalid) {
         return;
      }

      const payload: PlansOrderRequestPayload = {
         id: planId,
         payment_type: this.paymentType,
         payer: this.payerControl.value,
      };

      await this.orderPlanState.run(
         () => HttpClient.post<PlansOrderResponse>('/plans/order', payload),
         {
            onSuccess: (resp) => {
               if (resp.status === 200) {
                  onSuccess?.(resp.message, resp);
                  return;
               }

               if (this.isAuthMessage(resp.message)) {
                  onAuthError?.();
                  return;
               }

               onError?.(resp.message || 'Could not order membership plan');
            },
            onError: (error) => {
               if (this.shouldTreatAsAuthError(error)) {
                  onAuthError?.();
                  return;
               }

               onError?.(error.message);
            },
         }
      );
   }

   private isAuthMessage(message?: string): boolean {
      return !!message && /token has expired/i.test(message);
   }

   private shouldTreatAsAuthError(error: ApiError): boolean {
      return this.isAuthMessage(error.message) || error.message === 'No response from server';
   }
}

function PlanPurchaseDialogView({
   plan,
   setOpen,
}: {
   plan: PurchasePlan;
   open: boolean;
   setOpen: (o: boolean) => void;
}) {
   const [planPurchase] = useState(() => new PlanPurchaseStore());
   const navigate = useNavigate();

   return (
      <Dialog.Content>
         <DialogHeader title="Оплата пакету" />

         <Flex justify="center">
            <img
               src={
                  planPurchase.paymentType === 'card'
                     ? '/Images/atm-card.png'
                     : '/Images/invoice.png'
               }
               width={250}
               style={{ aspectRatio: '4 / 3' }}
            />
         </Flex>

         <RadioGroup.Root
            size="3"
            color="amber"
            variant="surface"
            value={planPurchase.paymentType}
            onValueChange={(e) => {
               planPurchase.setPaymentType(e as PlansOrderPaymentType);
            }}
         >
            <Flex gap="4" justify="center">
               <RadioGroup.Item value="card">
                  <Text color="gray" highContrast={planPurchase.paymentType === 'card'}>
                     Карткою
                  </Text>
               </RadioGroup.Item>
               <RadioGroup.Item value="invoice">
                  <Text color="gray" highContrast={planPurchase.paymentType === 'invoice'}>
                     Створити рахунок
                  </Text>
               </RadioGroup.Item>
            </Flex>
         </RadioGroup.Root>

         <Card my="6">
            <DataList.Root style={{ marginBottom: 0 }}>
               <DataList.Item align="center">
                  <DataList.Label minWidth="88px">Пакет:</DataList.Label>
                  <DataList.Value>
                     <Text> {plan.plan_name}</Text>
                  </DataList.Value>
               </DataList.Item>
               <DataList.Item>
                  <DataList.Label minWidth="88px">Ціна</DataList.Label>
                  <DataList.Value>
                     <Flex align="center" gap="2">
                        <Text> {plan.amount}</Text>
                        <Text> {plan.currency}</Text>
                     </Flex>
                  </DataList.Value>
               </DataList.Item>
            </DataList.Root>
         </Card>

         <FormField label="Платник" error={planPurchase.payerError}>
            <TextField.Root
               color="indigo"
               size="3"
               placeholder="Введіть інформацію про платника (ТОВ/ ФОП)"
               value={planPurchase.payer}
               onChange={(e) => planPurchase.setPayer(e.target.value)}
            ></TextField.Root>
         </FormField>

         <Flex justify="between" mt="6" align="center">
            <Dialog.Close>
               <Button size="3" variant="outline" radius="small">
                  Скасувати
               </Button>
            </Dialog.Close>

            <Separator orientation="vertical" style={{ minHeight: '40px' }} />
            <Button
               size="3"
               radius="small"
               loading={planPurchase.ordering}
               onClick={() => {
                  planPurchase.orderPlan({
                     planId: plan.plan_id,
                     onSuccess: async (_, response) => {
                        if (response?.response?.payment_url) {
                           window.open(response.response.payment_url, '_blank');
                           setOpen(false);
                        }

                        if (response?.response?.invoice_url) {
                           window.open(response?.response?.invoice_url, '_blank');
                           setOpen(false);
                           setTimeout(() => {
                              navigate('/user/paymenthistory');
                           }, 50);
                        }
                     },
                     onError: (message) => {
                        toast.error('Щось пійшло не так, спробуйте пізніше');
                     },
                  });
               }}
            >
               Продовжити
            </Button>
         </Flex>
      </Dialog.Content>
   );
}

export const PlanPurchaseDialog = observer(PlanPurchaseDialogView);
